import { useMemo, useState } from 'react';
import { Typography, Card, Row, Col, Spin, Tag, Popover, Tooltip, Modal, Button } from 'antd';
import {
  ShoppingOutlined,
  CheckCircleFilled,
  SyncOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  RiseOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  CalculatorOutlined,
  PieChartOutlined,
  PercentageOutlined,
  FundOutlined,
  BarChartOutlined,
  LineChartOutlined,
  ExperimentOutlined,
  RocketOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import './service-metrics.css';

const { Title, Text } = Typography;

const ServiceMetrics = ({ data, loading, currentDateInfo, onServiceCardClick }) => {
  const [activePopover, setActivePopover] = useState(null);
  const [isEfficiencyModalVisible, setIsEfficiencyModalVisible] = useState(false);
  const metrics = data || {};

  const metricsData = useMemo(() => {
    const totalServices = (metrics.services_by_category || []).reduce((sum, cat) => sum + (cat.count || 0), 0);
    const deliveredServices = (metrics.services_by_status || []).find(s => s.status_code === 7)?.count || 0;
    const inProgressServices = (metrics.services_by_status || []).reduce((sum, status) => {
      return [4, 5, 6].includes(status.status_code) ? sum + (status.count || 0) : sum;
    }, 0);
    const pendingServices = (metrics.services_by_status || []).reduce((sum, status) => {
      return [1, 2, 3].includes(status.status_code) ? sum + (status.count || 0) : sum;
    }, 0);
    const problemServices = (metrics.services_by_status || []).reduce((sum, status) => {
      return [8, 9, 10, 11, 12].includes(status.status_code) ? sum + (status.count || 0) : sum;
    }, 0);

    const pendingDetails = (metrics.services_by_status || []).filter(s => [1, 2, 3].includes(s.status_code));
    const inProgressDetails = (metrics.services_by_status || []).filter(s => [4, 5, 6].includes(s.status_code));
    const problemDetails = (metrics.services_by_status || []).filter(s => [8, 9, 10, 11, 12].includes(s.status_code));

    const activeProcessTotal = deliveredServices + inProgressServices + pendingServices;
    const operationalEfficiency = activeProcessTotal > 0
      ? Math.round((deliveredServices / activeProcessTotal) * 100)
      : 0;

    return {
      totalServices,
      deliveredServices,
      inProgressServices,
      pendingServices,
      problemServices,
      pendingDetails,
      inProgressDetails,
      problemDetails,
      completionRate: totalServices > 0 ? Math.round((deliveredServices / totalServices) * 100) : 0,
      operationalEfficiency,
      operationalEfficiencyDetails: {
        delivered: deliveredServices,
        inProgress: inProgressServices,
        pending: pendingServices,
        total: activeProcessTotal,
        formula: '(Servicios Entregados / (Entregados + En Progreso + Pendientes)) × 100',
        calculation: activeProcessTotal > 0
          ? `(${deliveredServices} / (${deliveredServices} + ${inProgressServices} + ${pendingServices})) × 100 = ${operationalEfficiency}%`
          : 'No hay servicios en proceso activo'
      },
      categories: metrics.services_by_category || []
    };
  }, [metrics]);

  const getStatusName = (statusCode) => {
    const statusMap = {
      1: 'En revisión',
      2: 'En cotización',
      3: 'Pendiente aprobación',
      4: 'En construcción',
      5: 'Inspección',
      6: 'En camino',
      7: 'Entregado',
      8: 'Rechazado',
      9: 'Cancelado',
      10: 'En corrección',
      11: 'Cancelación solicitada',
      12: 'En queja'
    };
    return statusMap[statusCode] || `Estado ${statusCode}`;
  };

  const handlePopoverClick = (index, event) => {
    event.stopPropagation();
    setActivePopover(activePopover === index ? null : index);
  };

  const handleDetailClick = (status, event) => {
    event.stopPropagation();
    if (onServiceCardClick) {
      onServiceCardClick(status);
    }
    setActivePopover(null);
  };

  const showEfficiencyModal = () => {
    setIsEfficiencyModalVisible(true);
  };

  const handleEfficiencyModalClose = () => {
    setIsEfficiencyModalVisible(false);
  };

  const renderStatusPopover = (details, title) => (
    <div className="status-popover-content">
      <div className="popover-header">
        <InfoCircleOutlined /> {title}
      </div>
      <div className="popover-list">
        {details.map((item, index) => (
          <div
            key={index}
            className="popover-item"
            onClick={(e) => handleDetailClick(item.status_code, e)}
          >
            <div className="popover-item-info">
              <span className="status-name">{getStatusName(item.status_code)}</span>
              <Tag color="blue" className="status-count">{item.count}</Tag>
            </div>
            <EyeOutlined className="view-icon" />
          </div>
        ))}
      </div>
    </div>
  );

  const serviceCards = [
    {
      title: 'Total de Servicios',
      value: metricsData.totalServices,
      icon: <ShoppingOutlined />,
      color: 'blue',
      subtitle: 'Todos los servicios registrados',
      trend: null,
      status: null,
      hasDetails: false,
      clickable: true
    },
    {
      title: 'Servicios entregados',
      value: metricsData.deliveredServices,
      icon: <CheckCircleFilled />,
      color: 'green',
      subtitle: 'Completados exitosamente',
      status: 7,
      hasDetails: false,
      clickable: true
    },
    {
      title: 'En progreso',
      value: metricsData.inProgressServices,
      icon: <SyncOutlined spin />,
      color: 'processing',
      subtitle: 'Construcción, inspección o en camino',
      details: metricsData.inProgressDetails,
      hasDetails: true,
      detailsTitle: 'Servicios en progreso',
      cardIndex: 2,
      clickable: true
    },
    {
      title: 'Pendientes',
      value: metricsData.pendingServices,
      icon: <ClockCircleOutlined />,
      color: 'gold',
      subtitle: 'Revisión, cotización o aprobación',
      details: metricsData.pendingDetails,
      hasDetails: true,
      detailsTitle: 'Servicios pendientes',
      cardIndex: 3,
      clickable: true
    },
    {
      title: 'Con incidencias',
      value: metricsData.problemServices,
      icon: <WarningOutlined />,
      color: 'red',
      subtitle: 'Rechazados, cancelados o en corrección',
      details: metricsData.problemDetails,
      hasDetails: true,
      detailsTitle: 'Servicios con incidencias',
      cardIndex: 4,
      clickable: true
    },
    {
      title: 'Eficiencia operativa',
      value: `${metricsData.operationalEfficiency}%`,
      icon: <PercentageOutlined />,
      color: 'purple',
      subtitle: 'Entregados vs proceso activo',
      trend: metricsData.operationalEfficiency >= 70 ? 'Alta'
        : metricsData.operationalEfficiency >= 40 ? 'Media'
          : 'Baja',
      status: null,
      hasDetails: false,
      clickable: false,
      showModalOnClick: true,
      details: metricsData.operationalEfficiencyDetails
    }
  ];

  const handleCardClick = (card, event) => {
    if (!card.clickable) {
      if (card.showModalOnClick) {
        showEfficiencyModal();
      }
      return;
    }

    if (card.hasDetails && card.details && card.details.length > 0) {
      handlePopoverClick(card.cardIndex, event);
    } else if (onServiceCardClick && card.status) {
      onServiceCardClick(card.status);
    } else if (onServiceCardClick && !card.hasDetails) {
      onServiceCardClick();
    }
  };

  const handleOutsideClick = () => {
    setActivePopover(null);
  };

  if (loading) {
    return (
      <Card className="dashboard-card">
        <div className="card-header">
          <div className="card-icon card-icon-blue">
            <ShoppingOutlined />
          </div>
          <div>
            <Title level={4}>Resumen Operativo</Title>
            <Text type="secondary">{currentDateInfo}</Text>
          </div>
        </div>
        <div className="card-content">
          <div className="kpi-grid">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <div key={index} className="service-metric-skeleton">
                <Spin size="small" />
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      {activePopover !== null && (
        <div className="popover-overlay" onClick={handleOutsideClick} />
      )}
      <Modal
        title={
          <div className="efficiency-modal-title">
            <DashboardOutlined /> Análisis de eficiencia operativa
          </div>
        }
        open={isEfficiencyModalVisible}
        onCancel={handleEfficiencyModalClose}
        footer={[
          <Button key="close" onClick={handleEfficiencyModalClose} icon={<InfoCircleOutlined />}>
            Cerrar
          </Button>
        ]}
        width={520}
        className="efficiency-modal"
      >
        <div className="efficiency-modal-content">
          {/* Resumen principal */}
          <div className="efficiency-summary">
            <div className="efficiency-value-large">
              <PercentageOutlined style={{ fontSize: 32, marginRight: 8 }} />
              {metricsData.operationalEfficiency}%
            </div>
            <Tag
              icon={metricsData.operationalEfficiency >= 70 ? <RocketOutlined /> : metricsData.operationalEfficiency >= 40 ? <ExperimentOutlined /> : <WarningOutlined />}
              color={metricsData.operationalEfficiency >= 70 ? 'success' : metricsData.operationalEfficiency >= 40 ? 'warning' : 'error'}
              className="efficiency-tag"
            >
              {metricsData.operationalEfficiency >= 70 ? 'Rendimiento óptimo' : metricsData.operationalEfficiency >= 40 ? 'Rendimiento moderado' : 'Rendimiento crítico'}
            </Tag>
          </div>

          <div className="efficiency-section">
            <h4><CalculatorOutlined /> Fórmula de cálculo</h4>
            <div className="efficiency-formula">
              <FundOutlined /> {metricsData.operationalEfficiencyDetails.formula}
            </div>
          </div>

          <div className="efficiency-section">
            <h4><BarChartOutlined /> Componentes del cálculo</h4>
            <div className="efficiency-values-grid">
              <div className="value-item">
                <span className="value-label">
                  <CheckCircleFilled style={{ color: '#52c41a' }} /> Entregados:
                </span>
                <span className="value-number">{metricsData.operationalEfficiencyDetails.delivered}</span>
              </div>
              <div className="value-item">
                <span className="value-label">
                  <SyncOutlined spin style={{ color: '#1890ff' }} /> En progreso:
                </span>
                <span className="value-number">{metricsData.operationalEfficiencyDetails.inProgress}</span>
              </div>
              <div className="value-item">
                <span className="value-label">
                  <ClockCircleOutlined style={{ color: '#faad14' }} /> Pendientes:
                </span>
                <span className="value-number">{metricsData.operationalEfficiencyDetails.pending}</span>
              </div>
              <div className="value-item total">
                <span className="value-label">
                  <PieChartOutlined /> Total proceso activo:
                </span>
                <span className="value-number">{metricsData.operationalEfficiencyDetails.total}</span>
              </div>
            </div>
          </div>

          <div className="efficiency-section">
            <h4><LineChartOutlined /> Desarrollo del cálculo</h4>
            <div className="efficiency-calculation">
              {metricsData.operationalEfficiencyDetails.calculation}
            </div>
          </div>

          <div className="efficiency-section">
            <h4><ExperimentOutlined /> Análisis del resultado</h4>
            <div className={`efficiency-interpretation ${metricsData.operationalEfficiency >= 70 ? 'success' : metricsData.operationalEfficiency >= 40 ? 'warning' : 'error'}`}>
              {metricsData.operationalEfficiency >= 70 ? (
                <p>
                  <RocketOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  <strong>Eficiencia alta:</strong> El {metricsData.operationalEfficiency}% de los servicios en proceso activo están siendo entregados exitosamente. La operación muestra un rendimiento óptimo con mínimos cuellos de botella.
                </p>
              ) : metricsData.operationalEfficiency >= 40 ? (
                <p>
                  <ExperimentOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  <strong>Eficiencia media:</strong> Con un {metricsData.operationalEfficiency}% de eficiencia, existe un equilibrio entre servicios entregados y en proceso. Se recomienda analizar los servicios pendientes y en progreso para identificar áreas de mejora.
                </p>
              ) : (
                <p>
                  <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                  <strong>Eficiencia baja:</strong> Solo el {metricsData.operationalEfficiency}% de los servicios en proceso activo se completan. Es prioritario revisar los flujos de trabajo, identificar cuellos de botella y optimizar los procesos operativos.
                </p>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Card className="dashboard-card">
        <div className="card-header">
          <div className="card-icon card-icon-blue">
            <ShoppingOutlined />
          </div>
          <div>
            <Title level={4}>Resumen Operativo</Title>
            <Text type="secondary">{currentDateInfo}</Text>
          </div>
        </div>

        <div className="card-content">
          <div className="service-metrics-grid">
            <Row gutter={[16, 16]}>
              {serviceCards.map((card, index) => (
                <Col xs={24} sm={12} md={8} lg={8} xl={4} key={index}>
                  {card.hasDetails && card.details && card.details.length > 0 ? (
                    <Popover
                      content={renderStatusPopover(card.details, card.detailsTitle)}
                      title={null}
                      trigger="click"
                      placement="top"
                      overlayClassName="status-popover"
                      open={activePopover === card.cardIndex}
                      onOpenChange={(visible) => {
                        if (!visible) {
                          setActivePopover(null);
                        }
                      }}
                    >
                      <Tooltip
                        title="Click para ver detalles por estado"
                        placement="top"
                        color="blue"
                      >
                        <Card
                          className={`service-metric-card ${activePopover === card.cardIndex ? 'popover-active' : ''}`}
                          size="small"
                          hoverable={card.clickable}
                          onClick={(e) => handleCardClick(card, e)}
                        >
                          <div className="service-metric-content">
                            <div className={`service-metric-icon service-metric-${card.color} has-details`}>
                              {card.icon}
                              <div className="icon-badge">
                                <InfoCircleOutlined />
                              </div>
                            </div>
                            <div className="service-metric-stats">
                              <div className="service-metric-value">{card.value}</div>
                              <div className="service-metric-title">{card.title}</div>
                              <div className="service-metric-subtitle">{card.subtitle}</div>
                            </div>
                          </div>
                        </Card>
                      </Tooltip>
                    </Popover>
                  ) : card.showModalOnClick ? (
                    <Tooltip
                      title="Click para ver análisis detallado"
                      placement="top"
                      color="purple"
                    >
                      <Card
                        className="service-metric-card"
                        size="small"
                        hoverable={true}
                        onClick={(e) => handleCardClick(card, e)}
                      >
                        <div className="service-metric-content">
                          <div className={`service-metric-icon service-metric-${card.color}`}>
                            {card.icon}
                          </div>
                          <div className="service-metric-stats">
                            <div className="service-metric-value">{card.value}</div>
                            <div className="service-metric-title">{card.title}</div>
                            <div className="service-metric-subtitle">{card.subtitle}</div>
                            {card.trend && (
                              <div className="service-metric-trend">
                                <Tag
                                  icon={card.trend === 'Alta' ? <RocketOutlined /> : card.trend === 'Media' ? <ExperimentOutlined /> : <WarningOutlined />}
                                  color={card.color === 'red' ? 'error' : card.color}
                                  size="small"
                                >
                                  {card.trend}
                                </Tag>
                              </div>
                            )}
                            <div className="metric-click-hint">
                              <CalculatorOutlined /> Ver análisis completo
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={card.status ? "Click para ver órdenes" : "Ver información"}
                      placement="top"
                    >
                      <Card
                        className="service-metric-card"
                        size="small"
                        hoverable={card.clickable && !!onServiceCardClick && card.status !== null}
                        onClick={(e) => handleCardClick(card, e)}
                      >
                        <div className="service-metric-content">
                          <div className={`service-metric-icon service-metric-${card.color}`}>
                            {card.icon}
                          </div>
                          <div className="service-metric-stats">
                            <div className="service-metric-value">{card.value}</div>
                            <div className="service-metric-title">{card.title}</div>
                            <div className="service-metric-subtitle">{card.subtitle}</div>
                            {card.trend && (
                              <div className="service-metric-trend">
                                <Tag
                                  color={card.color === 'red' ? 'error' : card.color}
                                  size="small"
                                >
                                  {card.trend}
                                </Tag>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    </Tooltip>
                  )}
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </Card>
    </>
  );
};

export default ServiceMetrics;